import { Alert, Button, Checkbox, Form, InputNumber, Radio, Select, Table, Tooltip, Row, Col } from "antd";
import { ArrowLeftOutlined, StopOutlined, InfoCircleFilled, PercentageOutlined } from '@ant-design/icons';
import { IPolicyCoverage } from "../../baseInfo/policyCoverage/interfaces";
import { IPolicyFranchise } from "../../baseInfo/policyFranchise/interfaces";
import { Core } from "../../components/infrastructure/core";
import { enmEntityState, ICallOptions, IEntity, IResultSet } from "../../components/infrastructure/models";
import { EvaluationComponent, IEvaluationComponentFormProps, IEvaluationComponentProps, IEvaluationComponentStates, IEvaluationInitialData } from "./evaluationComponent";
import React from "react";
import TextArea from "antd/lib/input/TextArea";
import { PriceInput } from "../../components/priceInput";
//import { Link } from "react-router-dom";
import { enmEvaluationStatus } from "../../baseInfo/damageCase/interfaces";
import { IDamageType } from "../../baseInfo/damageType/interfaces";
import '../index.css';
import AppContext from "../../components/infrastructure/appContext";

type LayoutType = Parameters<typeof Form>[0]['layout'];

const { Option } = Select;

interface ISendingItem {
    Id: number;
    Name: string;
    IsAvailable: boolean;
    Price?: number;
}

export interface IDamageEstimation extends IEntity {
    DamageCaseEvaluatorId: number;
    PolicyCoverageId: number;
    PolicyCoverage?: IPolicyCoverage;
    DamageTypeId?: number;
    DamageType?: IDamageType;
    InsuredValue: number;
    EquipmentPrice: number;

    Franchise1Id: number;
    Franchise1?: IPolicyFranchise;
    Franchise1DepreciationPercent?: number;
    Franchise1Amount: number;
    Franchise1Percent?: number;
    Franchise1Description: string;
    Franchise1DepreciationAmount: number;
    Franchise1EstimatedAmount: number;
    Franchise1Made10?: number;
    Franchise1PayableAmount?: number;

    HasFranchise2?: boolean;

    Franchise2Id?: number;
    Franchise2?: IPolicyFranchise;
    Franchise2DepreciationPercent?: number;
    Franchise2Amount: number;
    Franchise2Percent?: number;
    Franchise2Description?: string;
    Franchise2DepreciationAmount: number;
    Franchise2EstimatedAmount?: number;
    Franchise2Made10?: number;
    Franchise2PayableAmount?: number;

    PayableAmount: number;
    CalculationsDescription?: string;
    SendingItemList: ISendingItem[];
    SendingItems: ISendingItem[];
    Deductions: number;
    ExpertConfirmation?: boolean;
    ExpertDescription?: string;

    EssentialsReceived?: boolean;
}

export interface IDamageEstimationStates extends IEvaluationComponentStates<IDamageEstimation> {
    policyCoverages: IPolicyCoverage[];
    policyFranchises: IPolicyFranchise[];
    damageTypes: IDamageType[];
    sendingItems: ISendingItem[];
    confirmPermission?: boolean;
}

export interface IDamageEstimationProps extends IEvaluationComponentProps {

}

interface IDamageEstimationInitialData extends IEvaluationInitialData {
    PolicyCoverages: IPolicyCoverage[];
    PolicyFranchises: IPolicyFranchise[];
    DamageTypes: IDamageType[];
    SendingItems: { Id: number; Name: string; IsAvailable: boolean; }[];
    ConfirmPermission?: boolean;
}

export interface IDamageEstimationFromProps extends IEvaluationComponentFormProps<IDamageEstimation> {
    confirmPermission?: boolean;
    policyCoverages: IPolicyCoverage[];
    policyFranchises: IPolicyFranchise[];
    sendingItems: ISendingItem[];
    damageTypes: IDamageType[];
    onChangeDamageType?: (value: any) => void;
    onChangePolicyCoverage: (value: any) => void;
    onChangeEquipmentPrice: (value: any) => void;
    //onChangeInsuredValue: (value: any) => void;
    onChangeSendingItemList: (items: ISendingItem[]) => void;
    onChangeFranchise1: (value: any) => void;
    //onChangeFranchise1Percent: (value: any) => void;
    onChangeFranchise1DepreciationPercent: (value: any) => void;
    onChangeFranchise1Description: (e: any) => void;
    onChangeFranchise1EstimatedAmount: (value: any) => void;
    onChangeHasFranchise2: (e: any) => void;
    onChangeFranchise2: (value: any) => void;
    //onChangeFranchise2Percent: (value: any) => void;
    onChangeFranchise2DepreciationPercent: (value: any) => void;
    onChangeFranchise2Description: (e: any) => void;
    onChangeFranchise2EstimatedAmount: (value: any) => void;
    onChangeExpertConfirmation: (e: any) => void;

    onChangeExpertDescription: (e: any) => void;
    onChangeEssentialsReceived: (e: any) => void;
}

const SendingItemList = (props: { disabled?: boolean; items: ISendingItem[], onChange: (items: ISendingItem[]) => void; }) => {
    const columns = [{
        title: 'نام',
        dataIndex: 'Name',
        key: 'Name'
    },
    {
        title: 'موجود است؟',
        dataIndex: 'IsAvailable',
        key: 'IsAvailable',
        render: (_: any, item: ISendingItem) => {
            return (
                <Checkbox defaultChecked={item.IsAvailable} disabled={props.disabled} onChange={(e) => {
                    props.items.find(i => i.Id === item.Id)!.IsAvailable = e.target.checked;
                    props.onChange(props.items);
                }} />
            );
        }
    },
    {
        title: 'قیمت',
        dataIndex: 'Price',
        key: 'Price',
        render: (_: any, item: ISendingItem) => {
            return (
                <PriceInput defaultValue={item.Price} disabled={props.disabled} style={{ width: "100%" }} onChange={(value) => {
                    props.items.find(i => i.Id === item.Id)!.Price = value;
                    props.onChange(props.items);
                }} />
            );
        }
    }];

    return (
        <Table rowKey={"Id"} columns={columns} dataSource={props.items} bordered pagination={false} />
    );
}

const calculateAmount = (currentEntity: IDamageEstimation) => {
    if (currentEntity.EquipmentPrice && currentEntity.InsuredValue) {
        let deductions = 0;

        currentEntity.SendingItemList.forEach(i => {
            if (i.Price && !i.IsAvailable)
                deductions += i.Price;
        });

        if (currentEntity.DamageTypeId === 2) {// برای خسارت جزئی
            let ratio = Number((currentEntity.InsuredValue / currentEntity.EquipmentPrice).toPrecision(4));
            if (currentEntity.Franchise1EstimatedAmount) {
                let damageAmount = Math.min(currentEntity.Franchise1EstimatedAmount ?? 0, currentEntity.EquipmentPrice);
                if (currentEntity.Franchise1DepreciationPercent) {// محاسبه استهلاک در فرانشیز اول
                    currentEntity.Franchise1DepreciationAmount = Math.ceil(damageAmount * currentEntity.Franchise1DepreciationPercent / 100);
                    damageAmount = Math.floor(damageAmount - currentEntity.Franchise1DepreciationAmount);
                }
                if (currentEntity.InsuredValue < currentEntity.EquipmentPrice) {//اعمال ماده 10
                    const made10 = Math.floor(damageAmount * ratio);
                    currentEntity.Franchise1Made10 = Math.ceil(damageAmount -  made10);
                    damageAmount =  made10;
                    deductions = Math.ceil(deductions * ratio);
                }
                let franchiseAmount = Math.ceil(damageAmount * (currentEntity.Franchise1Percent ?? 0) / 100);
                if (franchiseAmount < (currentEntity.Franchise1?.MinimumValue ?? 0))// اعمال حداقل فرانشیز
                    franchiseAmount = currentEntity.Franchise1?.MinimumValue ?? 0;
                let payableAmount = damageAmount - franchiseAmount;
                currentEntity.Franchise1Amount = franchiseAmount + deductions;
                currentEntity.Franchise1PayableAmount = payableAmount < 0 ? 0 : payableAmount;
            }
            if (currentEntity.HasFranchise2 === true && currentEntity.Franchise2EstimatedAmount) {
                let damageAmount = Math.min(currentEntity.Franchise2EstimatedAmount, currentEntity.EquipmentPrice);
                if (currentEntity.Franchise2DepreciationPercent) {// محاسبه استهلاک در فرانشیز دوم
                    currentEntity.Franchise2DepreciationAmount = Math.ceil(damageAmount * currentEntity.Franchise2DepreciationPercent / 100);
                    damageAmount = Math.floor(damageAmount - currentEntity.Franchise2DepreciationAmount);
                }
                if (currentEntity.InsuredValue < currentEntity.EquipmentPrice) {//اعمال ماده 10
                    const made10 = Math.floor(damageAmount * ratio);
                    currentEntity.Franchise2Made10 = Math.ceil(damageAmount - made10);
                    damageAmount = made10;
                    deductions = Math.ceil(deductions * ratio);
                }
                let franchiseAmount = Math.ceil(damageAmount * (currentEntity.Franchise2Percent ?? 0) / 100);
                if (franchiseAmount < (currentEntity.Franchise2?.MinimumValue ?? 0))// اعمال حداقل فرانشیز
                    franchiseAmount = currentEntity.Franchise2?.MinimumValue ?? 0;
                let payableAmount = damageAmount - franchiseAmount;
                currentEntity.Franchise2Amount = franchiseAmount + deductions;
                currentEntity.Franchise2PayableAmount = payableAmount < 0 ? 0 : payableAmount;
            }

            if (deductions && currentEntity.InsuredValue < currentEntity.EquipmentPrice)// اعمال ماده 10 روی کسورات اقلام ناقص
                deductions = Math.ceil(deductions * ratio);

            const payableAmount = (currentEntity.Franchise1PayableAmount ?? 0) + (currentEntity.HasFranchise2 === true ? currentEntity.Franchise2PayableAmount ?? 0 : 0) - deductions;
            currentEntity.PayableAmount = payableAmount > 0 ? payableAmount : 0;
        }
        else { // برای خسارت کلی و سرقت
            let damageAmount = Math.min(currentEntity.InsuredValue ?? 0, currentEntity.EquipmentPrice);
            currentEntity.Franchise1EstimatedAmount = damageAmount;
            damageAmount -= deductions;

            let franchiseAmount = Math.ceil(damageAmount * (currentEntity.Franchise1Percent ?? 0) / 100);
            if (franchiseAmount < (currentEntity.Franchise1?.MinimumValue ?? 0))// اعمال حداقل فرانشیز
                franchiseAmount = currentEntity.Franchise1?.MinimumValue ?? 0;
            let payableAmount = damageAmount - franchiseAmount;
            currentEntity.Deductions = deductions;
            currentEntity.Franchise1Amount = franchiseAmount;
            currentEntity.Franchise1PayableAmount = payableAmount;

            //if (currentEntity.HasFranchise2 === true && currentEntity.Franchise2EstimatedAmount) {
            //    let franchiseAmount = Math.ceil(damageAmount * (currentEntity.Franchise2Percent ?? 0) / 100);
            //    if (franchiseAmount < (currentEntity.Franchise2?.MinimumValue ?? 0))// اعمال حداقل فرانشیز
            //        franchiseAmount = currentEntity.Franchise2?.MinimumValue ?? 0;
            //    let payableAmount = damageAmount - franchiseAmount;
            //    currentEntity.Franchise2Amount = franchiseAmount;
            //    currentEntity.Franchise2PayableAmount = payableAmount;

            //}
            //const payableAmount = (currentEntity.Franchise1PayableAmount ?? 0) + (currentEntity.HasFranchise2 === true ? currentEntity.Franchise2PayableAmount ?? 0 : 0);
            currentEntity.PayableAmount = payableAmount > 0 ? payableAmount : 0;
        }
    }
}

export const DamageEstimationForm = (props: IDamageEstimationFromProps) => {

    const formItemLayout = {
        labelCol: { xxl: 6, xl: 6, lg: 6, md: 6, sm: 6, xs: 6 },
        wrapperCol: { xxl: 12, xl: 16, lg: 18, md: 18, sm: 18, xs: 18 },
    };

    const [formLayout] = React.useState<LayoutType>('horizontal');
    const [form] = Form.useForm();
    const { getUserInfo } = React.useContext(AppContext);
    const userInfo = getUserInfo();

    const evaluatorId = userInfo?.extraVariables['EvaluatorId'];
    const { DamageCaseEvaluators } = props.damageCase;
    const currentEvaluatorId = DamageCaseEvaluators[DamageCaseEvaluators.length - 1].EvaluatorId;

    React.useEffect(() => {
        let currentEntity = props.currentEntity;
        currentEntity.EquipmentPrice = currentEntity.EquipmentPrice ?? 0;
        currentEntity.InsuredValue = currentEntity.InsuredValue ?? 0;
        currentEntity.Deductions = currentEntity.Deductions ?? 0;

        currentEntity.Franchise1DepreciationPercent = currentEntity.Franchise1DepreciationPercent ?? 0;
        currentEntity.Franchise1Amount = currentEntity.Franchise1Amount ?? 0;
        currentEntity.Franchise1EstimatedAmount = currentEntity.Franchise1EstimatedAmount ?? 0;
        currentEntity.Franchise1DepreciationAmount = currentEntity.Franchise1DepreciationAmount ?? 0;
        currentEntity.Franchise1Percent = currentEntity.Franchise1Percent ?? currentEntity.Franchise1?.FranchisePercent ?? 0;
        currentEntity.Franchise1PayableAmount = currentEntity.Franchise1PayableAmount ?? 0;

        currentEntity.HasFranchise2 = currentEntity.HasFranchise2 ?? false;

        currentEntity.Franchise2DepreciationPercent = currentEntity.Franchise2DepreciationPercent ?? 0;
        currentEntity.Franchise2Amount = currentEntity.Franchise2Amount ?? 0;
        currentEntity.Franchise2EstimatedAmount = currentEntity.Franchise2EstimatedAmount ?? 0;
        currentEntity.Franchise2DepreciationAmount = currentEntity.Franchise2DepreciationAmount ?? 0;
        currentEntity.Franchise2Percent = currentEntity.Franchise2Percent ?? currentEntity.Franchise2?.FranchisePercent ?? 0;
        currentEntity.Franchise2Amount = currentEntity.Franchise2PayableAmount ?? 0;

        currentEntity.PayableAmount = currentEntity.PayableAmount ?? 0;

        if (props.confirmPermission === true && currentEvaluatorId === evaluatorId && evaluatorId === 1) {
            // اگر ارزیاب جاری زبده باشد پیشفرض برآورد تایید شود
            if (currentEntity.ExpertConfirmation === undefined && (!props.damageCase.ReceiveDepreciated || currentEntity.DamageTypeId === 3))
                currentEntity.ExpertConfirmation = currentEntity.DamageTypeId !== 1 || currentEntity.EssentialsReceived ? true : undefined;

            // اگر ارزیاب جاری زبده باشد و خسارت کلی بشد بصورت پیشفرض دستگاه و لوازم جانبی دریافت شده در نظر گرفته میشود
            if (currentEntity.EssentialsReceived !== true && currentEntity.EssentialsReceived !== false && currentEntity.DamageTypeId === 1)
                currentEntity.EssentialsReceived = true;
        }

        if (!currentEntity.SendingItemList)
            currentEntity.SendingItemList = currentEntity.SendingItems.map((s: any) => { return { Id: s.SendingItemId, IsAvailable: s.IsAvailable, Price: s.Price, Name: s.SendingItem.Name }; });

        calculateAmount(currentEntity);
        form.setFieldsValue(currentEntity);
    }, [props, form]);

    const policyCoverageOptions = props.policyCoverages
        ? props.policyCoverages.map(o => <Option key={o.Id} value={o.Id}>{o.CoverageTitle}</Option>)
        : [<Option key={props.currentEntity.PolicyCoverageId} value={props.currentEntity.PolicyCoverage?.Id}>{props.currentEntity.PolicyCoverage?.CoverageTitle}</Option>];

    const policyFranchise1Options = props.policyFranchises
        ? props.policyFranchises.filter(f => f.DamageTypeId === props.currentEntity?.DamageTypeId).map(o => <Option key={o.Id} value={o.Id}>{o.FranchiseTitle}</Option>)
        : [<Option key={props.currentEntity.Franchise1Id} value={props.currentEntity.Franchise1?.Id}>{props.currentEntity.Franchise1?.FranchiseTitle}</Option>];

    const policyFranchise2Options = props.policyFranchises
        ? props.policyFranchises.filter(f => f.DamageTypeId === props.currentEntity?.DamageTypeId).map(o => <Option key={o.Id} value={o.Id}>{o.FranchiseTitle}</Option>)
        : [<Option key={props.currentEntity.Franchise2Id} value={props.currentEntity.Franchise2?.Id}>{props.currentEntity.Franchise2?.FranchiseTitle}</Option>];

    const damageTypesOptions = props.damageTypes
        ? props.damageTypes.map(o => <Option key={o.Id} value={o.Id}>{o.Name}</Option>)
        : [<Option key={props.currentEntity.DamageTypeId} value={props.currentEntity.DamageType?.Id}>{props.currentEntity.DamageType?.Name}</Option>];

    //const sendingItemsOptions = props.sendingItems
    //    ? props.sendingItems.map(o => <Option key={o.Id} value={o.Id}>{o.Name}</Option>)
    //    : props.currentEntity.SendingItems.map(o => <Option key={o.Id} value={o.Id}>{o.Name}</Option>);

    const sendingItems = props.sendingItems
        ? props.sendingItems
        : props.currentEntity.SendingItems.map((s: any) => { return { Id: s.SendingItemId, IsAvailable: s.IsAvailable, Price: s.Price, Name: s.SendingItem.Name }; });

    //const returnUrl = encodeURIComponent('/damagecaseevaluator?id=' + props.damageCase.RowGuid);

    //const expertConfirmation = props.currentEntity.ExpertConfirmation !== null ? (props.currentEntity.ExpertConfirmation === true ? "confirm" : "reject") : null;

    const equipmentPriceValidator = () => {
        const { currentEntity } = props;
        if (!currentEntity.EquipmentPrice)
            return Promise.reject(new Error('سرمایه بیمه نامه اجباری است'));
        return Promise.resolve();
    }

    return (
        <>
            <Form
                scrollToFirstError
                form={form}
                {...formItemLayout}
                layout={formLayout}
                onFinish={async (values) => {
                    props.onSubmit && await props.onSubmit(values);
                }}
                onReset={() => {
                    props.onClose && props.onClose(this);
                }}
            >
                <div key="maininfo">
                    <Form.Item
                        label={'پوشش'}
                        name={'PolicyCoverageId'}
                        key={'PolicyCoverageId'}
                        rules={[{ required: props.confirmPermission, message: 'انتخاب پوشش اجباری است' }]}
                    >
                        <Select disabled={props.disabled}
                            onChange={props.onChangePolicyCoverage}
                            key={'PolicyCoverageId'}
                        >
                            {policyCoverageOptions}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={'نوع خسارت'}
                        name={'DamageTypeId'}
                        key={'DamageTypeId'}
                        rules={[{ required: true, message: 'نوع خسارت اجباری است' }]}
                    >
                        <Select
                            disabled={props.disabled}
                            onChange={data => {
                                form.resetFields(['Franchise1Id', 'Franchise2Id']);
                                props.onChangeDamageType && props.onChangeDamageType(data);
                            }}
                            key={'DamageTypeId'}
                        >
                            {damageTypesOptions}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={'سرمایه بیمه نامه'}
                        name={'InsuredValue'}
                        key={'InsuredValue'}
                        initialValue={props.currentEntity.InsuredValue}
                        rules={[{ required: props.confirmPermission, message: 'سرمایه بیمه نامه اجباری است' }]}
                    >
                        <PriceInput
                            disabled={true}
                            //onChange={props.onChangeInsuredValue}
                            style={{ width: "100%" }}
                        />
                    </Form.Item>

                    <Form.Item
                        label={'قیمت روز دستگاه'}
                        name={'EquipmentPrice'}
                        key={'EquipmentPrice'}
                        rules={[{ required: props.confirmPermission, validator: equipmentPriceValidator }]}
                        initialValue={props.currentEntity.EquipmentPrice}
                    >
                        <PriceInput
                            disabled={props.disabled}
                            onChange={props.onChangeEquipmentPrice}
                            style={{ width: "100%" }}
                        />
                    </Form.Item>


                    {props.currentEntity.DamageTypeId === 2 && props.currentEntity.InsuredValue < props.currentEntity.EquipmentPrice &&
                        // برای خسارت جزئی ماده ده نمایش داده شود
                        <Col xxl={18} xl={22} lg={24} md={24} sm={24} xs={24} >
                            <Alert type="warning" showIcon style={{ marginTop: 10, marginBottom: 10 }}
                                message={
                                    <Row wrap style={{ margin: 0 }}>
                                        <Col lg={8} md={8} sm={8} xs={8} >
                                            <span>ضریب ماده 10: </span>
                                        </Col>
                                        <Col lg={16} md={16} sm={16} xs={16}>
                                            <b>{`   ${(props.currentEntity.InsuredValue / props.currentEntity.EquipmentPrice).toPrecision(4).replace('.', '/')}  `}</b>
                                        </Col>
                                    </Row>
                                }
                                icon={
                                    <Tooltip placement='left' title='درصورتی که مالی کمتر از قیمت واقعی(قیمت روز حادثه) بیمه شده باشد، بیمه‌گر فقط به تناسب مبلغ بیمه شده نسبت به قیمت واقعی مال، مسئول و متعهد به پرداخت خسارت خواهد بود'>
                                        <InfoCircleFilled style={{ marginLeft: 5, marginRight: 5, verticalAlign: 'middle', color: 'lightskyblue' }} />
                                    </Tooltip>
                                }
                            />
                        </Col>
                    }

                    {sendingItems.length ?
                        <Form.Item
                            label={'اقلام لازم جهت ارسال'}
                            name={'SendingItemList'}
                            key={'SendingItemList'}
                            initialValue={props.currentEntity.SendingItems.filter(s => s.IsAvailable).map(s => s.Id)}
                        >
                            <SendingItemList
                                items={sendingItems}
                                onChange={props.onChangeSendingItemList}
                                disabled={props.disabled}
                            />
                        </Form.Item> :
                        <></>
                    }
                </div>

                <div key="franchise1" style={{ paddingTop: 10, marginTop: 10, borderTop: '2px solid #e5e5e5' }}>
                    <Form.Item
                        label={
                            <span>
                                فرانشیز اول
                                <Tooltip placement='left' title='بخشی از خسارت توسط بیمه‌گذار یا خود فردی که دچار خسارت شده پرداخت میشود مبلغ فرانشیز نام دارد'>
                                    <InfoCircleFilled style={{ marginLeft: 1, marginRight: 5, verticalAlign: 'middle', color: 'lightskyblue' }} />
                                </Tooltip>
                            </span>
                        }
                        name={'Franchise1Id'}
                        key={'Franchise1Id'}
                        rules={[{ required: props.confirmPermission, message: 'انتخاب فرانشیز اجباری است' }]}
                    >
                        <Select disabled={props.disabled || !props.currentEntity?.DamageTypeId}
                            onChange={props.onChangeFranchise1}
                            key={'Franchise1Id'}
                        >
                            {policyFranchise1Options}
                        </Select>
                    </Form.Item>

                    {props.currentEntity.DamageTypeId === 2 &&
                        <Form.Item
                            label={'درصد استهلاک'}
                            name={'Franchise1DepreciationPercent'}
                            key={'Franchise1DepreciationPercent'}
                            initialValue={props.currentEntity.Franchise1DepreciationPercent}
                        >
                            <InputNumber
                                disabled={props.disabled}
                                onChange={props.onChangeFranchise1DepreciationPercent}
                                style={{ width: "100%" }}
                                //precision={2}
                                prefix={<PercentageOutlined />}
                            />
                        </Form.Item>
                    }

                    <Form.Item
                        label={'درصد فرانشیز'}
                        name={'Franchise1Percent'}
                        key={'Franchise1Percent'}
                    >
                        <InputNumber
                            disabled={true}
                            //formatter={value => {
                            //    return `% ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                            //}}
                            //onChange={props.onChangeFranchise1Percent}
                            style={{ width: "100%" }}
                            prefix={<PercentageOutlined />}
                        />
                    </Form.Item>

                    <Form.Item
                        label={'توضیحات'}
                        name={'Franchise1Description'}
                        key={'Franchise1Description'}
                        rules={[{ required: true, message: 'توصیحات اجباری است' }]}
                    >
                        <TextArea rows={5} onChange={props.onChangeFranchise1Description} disabled={props.disabled} />
                    </Form.Item>

                    <Form.Item
                        label={'مبلغ برآورد شده'}
                        name={'Franchise1EstimatedAmount'}
                        key={'Franchise1EstimatedAmount'}
                        rules={[{ required: true, message: 'مبلغ برآورد شده اجباری است' }]}
                        initialValue={props.currentEntity.Franchise1EstimatedAmount}
                    >
                        <PriceInput disabled={props.disabled || props.currentEntity.DamageTypeId !== 2}
                            onChange={props.onChangeFranchise1EstimatedAmount}
                            style={{ width: "100%" }}
                        />
                    </Form.Item>

                    {props.currentEntity.DamageTypeId === 2 &&
                        <Form.Item
                            label={'مبلغ استهلاک'}
                            name={'Franchise1DepreciationAmount'}
                            key={'Franchise1DepreciationAmount'}
                            initialValue={props.currentEntity.Franchise1DepreciationAmount}
                        >
                            <PriceInput disabled
                                style={{ width: "100%" }}
                            />
                        </Form.Item>
                    }

                    {props.currentEntity.DamageTypeId !== 2 &&
                        <Form.Item
                            label={'مبلغ کسورات'}
                            name={'Deductions'}
                            key={'Deductions'}
                            initialValue={props.currentEntity.Deductions}
                        >
                            <PriceInput disabled
                                style={{ width: "100%" }}
                            />
                        </Form.Item>
                    }

                    {props.currentEntity.DamageTypeId === 2 && props.currentEntity.InsuredValue < props.currentEntity.EquipmentPrice &&
                        <Form.Item
                            label={'کسورات ماده 10'}
                        >
                            <PriceInput disabled
                                style={{ width: "100%" }}
                                //value={props.currentEntity.Franchise1EstimatedAmount - props.currentEntity.Franchise1DepreciationAmount - Math.ceil(Number((props.currentEntity.InsuredValue / props.currentEntity.EquipmentPrice).toPrecision(4)) * props.currentEntity.Franchise1EstimatedAmount)}
                                value={props.currentEntity.Franchise1Made10}
                            />
                        </Form.Item>
                    }

                    <Form.Item
                        label={'مبلغ فرانشیز'}
                        name={'Franchise1Amount'}
                        key={'Franchise1Amount'}
                        initialValue={props.currentEntity.Franchise1Amount}
                    >
                        <PriceInput disabled
                            style={{ width: "100%" }}
                        />
                    </Form.Item>

                    {props.currentEntity?.DamageTypeId === 2 &&
                        <Form.Item
                            label={'دارای فرانشیز دوم'}
                            name={'HasFranchise2'}
                            key={'HasFranchise2'}
                        >
                            <Radio.Group key={'HasFranchise2'} onChange={props.onChangeHasFranchise2} disabled={props.disabled} value={props.currentEntity.HasFranchise2}>
                                <Radio key={"yes"} value={true}> بله </Radio>
                                <Radio key={"no"} value={false}> خیر </Radio>
                            </Radio.Group>
                        </Form.Item>
                    }
                </div>

                {props.currentEntity.HasFranchise2 === true &&
                    <div key="franchise2" style={{ paddingTop: 10, marginTop: 10, borderTop: '2px solid #e5e5e5' }}>
                        <Form.Item
                            label={
                                <span>
                                    فرانشیز دوم
                                    <Tooltip placement='left' title='بخشی از خسارت توسط بیمه‌گذار یا خود فردی که دچار خسارت شده پرداخت میشود مبلغ فرانشیز نام دارد'>
                                        <InfoCircleFilled style={{ marginLeft: 1, marginRight: 5, verticalAlign: 'middle', color: 'lightskyblue' }} />
                                    </Tooltip>
                                </span>
                            }
                            name={'Franchise2Id'}
                            key={'Franchise2Id'}
                            rules={[{ required: props.confirmPermission, message: 'انتخاب فرانشیز اجباری است' }]}
                        >
                            <Select disabled={props.disabled || !props.currentEntity?.DamageTypeId}
                                onChange={props.onChangeFranchise2}
                                key={'Franchise2Id'}
                            >
                                {policyFranchise2Options}
                            </Select>
                        </Form.Item>

                        {props.currentEntity.DamageTypeId === 2 &&
                            <Form.Item
                                label={'درصد استهلاک'}
                                name={'Franchise2DepreciationPercent'}
                                key={'Franchise2DepreciationPercent'}
                                initialValue={props.currentEntity.Franchise2DepreciationPercent}
                            >
                                <InputNumber
                                    disabled={props.disabled}
                                    onChange={props.onChangeFranchise2DepreciationPercent}
                                    style={{ width: "100%" }}
                                    
                                    //precision={2}
                                    prefix={<PercentageOutlined />}
                                />
                            </Form.Item>
                        }

                        <Form.Item
                            label={'درصد فرانشیز'}
                            name={'Franchise2Percent'}
                            key={'Franchise2Percent'}
                        >
                            <InputNumber disabled={true}
                                //formatter={value => {
                                //    return `% ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                                //}}
                                //onChange={props.onChangeFranchise2Percent}
                                
                                prefix={<PercentageOutlined />}
                                style={{ width: "100%" }}
                            />
                        </Form.Item>

                        <Form.Item
                            label={'توضیحات'}
                            name={'Franchise2Description'}
                            key={'Franchise2Description'}
                            rules={[{ required: true, message: 'توصیحات اجباری است' }]}
                        >
                            <TextArea rows={5} onChange={props.onChangeFranchise2Description} disabled={props.disabled} />
                        </Form.Item>

                        <Form.Item
                            label={'مبلغ برآورد شده'}
                            name={'Franchise2EstimatedAmount'}
                            key={'Franchise2EstimatedAmount'}
                            rules={[{ required: true, message: 'مبلغ برآورد شده اجباری است' }]}
                            initialValue={props.currentEntity.Franchise2EstimatedAmount}
                        >
                            <PriceInput disabled={props.disabled}
                                onChange={props.onChangeFranchise2EstimatedAmount}
                                style={{ width: "100%" }}
                            />
                        </Form.Item>

                        {props.currentEntity.DamageTypeId === 2 &&
                            <Form.Item
                                label={'مبلغ استهلاک'}
                                name={'Franchise2DepreciationAmount'}
                                key={'Franchise2DepreciationAmount'}
                                initialValue={props.currentEntity.Franchise1DepreciationAmount}
                            >
                                <PriceInput disabled
                                    style={{ width: "100%" }}
                                />
                            </Form.Item>
                        }

                        {props.currentEntity.DamageTypeId === 2 && props.currentEntity.InsuredValue < props.currentEntity.EquipmentPrice &&
                            <Form.Item
                                label={'کسورات ماده 10'}
                            >
                                <PriceInput disabled
                                    style={{ width: "100%" }}
                                    //value={(props.currentEntity.Franchise2EstimatedAmount ?? 0) - props.currentEntity.Franchise2DepreciationAmount - Math.ceil(Number((props.currentEntity.InsuredValue / props.currentEntity.EquipmentPrice).toPrecision(4)) * (props.currentEntity.Franchise2EstimatedAmount ?? 0))}
                                    value={props.currentEntity.Franchise2Made10}
                                />
                            </Form.Item>
                        }

                        <Form.Item
                            label={'مبلغ فرانشیز'}
                            name={'Franchise2Amount'}
                            key={'Franchise2Amount'}
                            initialValue={props.currentEntity.Franchise2Amount}
                        >
                            <PriceInput disabled
                                style={{ width: "100%" }}
                            />
                        </Form.Item>
                    </div>
                }
                <div key="payableamount" style={{ paddingTop: 10, marginTop: 10, borderTop: '2px solid #e5e5e5' }}>
                    <Form.Item
                        label={<b>مبلغ پرداختی</b>}
                        name={'PayableAmount'}
                        key={'PayableAmount'}
                        initialValue={props.currentEntity.PayableAmount}
                    >
                        <PriceInput disabled
                            style={{ width: "100%" }}
                        />
                    </Form.Item>
                    {props.currentEntity.DamageTypeId === 2 && props.currentEntity.InsuredValue < props.currentEntity.EquipmentPrice && props.currentEntity.PayableAmount > 0 &&
                        <Col xxl={18} xl={22} lg={24} md={24} sm={24} xs={24} >
                            <Alert type="warning" showIcon style={{ marginTop: 10, marginBottom: 10 }} message={<b>ماده 10 اعمال شده است</b>} icon={
                                <Tooltip placement='left' title='درصورتی که مالی کمتر از قیمت واقعی(قیمت روز حادثه) بیمه شده باشد، بیمه‌گر فقط به تناسب مبلغ بیمه شده نسبت به قیمت واقعی مال، مسئول و متعهد به پرداخت خسارت خواهد بود'>
                                    <InfoCircleFilled style={{ marginLeft: 5, marginRight: 5, verticalAlign: 'middle', color: 'lightskyblue' }} />
                                </Tooltip>
                            } />
                        </Col>
                    }
                </div>

                {props.confirmPermission === true || props.currentEntity.ExpertConfirmation === true || props.currentEntity.ExpertConfirmation === false ?
                    <div key="confirmation" style={{ paddingTop: 10, marginTop: 10, borderTop: '2px solid #e5e5e5' }}>
                        {((currentEvaluatorId !== evaluatorId && evaluatorId === 1) || props.disabled || props.damageCase.ReceiveDepreciated) &&
                            <Form.Item
                                label={'ارزیابی مورد تایید است؟'}
                                name={'ExpertConfirmation'}
                                key={'ExpertConfirmation'}
                            >
                                <Radio.Group
                                    key={'ExpertConfirmation'}
                                    onChange={props.onChangeExpertConfirmation}
                                    disabled={props.disabled || !props.confirmPermission}
                                    value={props.currentEntity.ExpertConfirmation}
                                >
                                    <Radio key={"confirm"} value={true}> تایید </Radio>
                                    <Radio key={"reject"} value={false}> رد </Radio>
                                </Radio.Group>
                            </Form.Item>
                        }
                        {
                            (currentEvaluatorId === evaluatorId && evaluatorId === 1 && props.currentEntity.DamageTypeId === 1) &&
                            <Form.Item
                                label={'دستگاه و لوازم جانبی دریافت شده است؟'}
                                name={'EssentialsReceived'}
                                key={'EssentialsReceived'}
                            >
                                <Radio.Group
                                    key={'EssentialsReceived'}
                                    onChange={props.onChangeEssentialsReceived}
                                    disabled={props.disabled || !props.confirmPermission}
                                    value={props.currentEntity.EssentialsReceived}
                                >
                                    <Radio key={"yes"} value={true}> بله </Radio>
                                    <Radio key={"no"} value={false}> خیر </Radio>
                                </Radio.Group>
                            </Form.Item>
                        }
                        <Form.Item
                            label={'توضیحات کارشناس'}
                            name={'ExpertDescription'}
                            key={'ExpertDescription'}
                            rules={[{ required: props.currentEntity.ExpertConfirmation === false, message: 'توصیحات کارشناس است' }]}
                        >
                            <TextArea rows={5} onChange={props.onChangeExpertDescription} disabled={props.disabled || !props.confirmPermission} />
                        </Form.Item>

                    </div>
                    : <></>
                }

                {props.disabled ? <></> :
                    <Form.Item wrapperCol={{ dir: 'ltr' }} style={{ zIndex: 1000, borderTop: '2px solid #e5e5e5', marginTop: 10, marginRight: 0 }}>
                        <Button type="primary" htmlType="submit" icon={<ArrowLeftOutlined />} loading={props.loading} style={{ width: "100px", marginTop: 10, marginBottom: 10 }}>
                            بعدی
                        </Button>

                        <Button type="ghost" htmlType="reset" icon={<StopOutlined />} loading={props.loading} style={{ width: "100px", marginTop: 10, marginBottom: 10, marginLeft: 10, background: '#fff' }}>
                            لغو
                        </Button>

                        {/*{props.policyAccess === true && <Link to={{ pathname: '/policy', search: '?id=' + props.damageCase.PolicyEquipmentModel.Policy?.RowGuid + '&returnurl=' + returnUrl + '&invokePath=برآورد خسارت' }} style={{ width: "180px", marginTop: 10, marginBottom: 10, marginLeft: 10 }}> ویرایش اطلاعات بیمه نامه</Link>}*/}

                    </Form.Item>
                }
            </Form>
        </>
    );
}

export class DamageEstimation extends EvaluationComponent<IDamageEstimation, IDamageEstimationProps, IDamageEstimationStates>{

    onInitializing() {
        const policyEquipmentModel = this.props.damageCase.PolicyEquipmentModel;

        this.defaultEntity = {
            ExpertConfirmation: undefined,
            SendingItems: [],
            SendingItemList: [],
            InsuredValue: policyEquipmentModel?.InsuredValue ?? 0,
            DamageTypeId: this.props.damageCase.DamageTypeId
        } as any;

        this.defaultState = {
            policyCoverages: [],
            policyFranchises: [],
            sendingItems: [],
            damageTypes: [],
            allDamageCases: []
        } as any;

        this.onInitialDataFetching = (params: any) => {
            params.damageCaseId = this.props.damageCase.Id;
            return params;
        };

        this.onInitialDataFetched = (result) => {
            let damageEstimationInitialData = result.Data as IDamageEstimationInitialData;
            const { DamageCaseEvaluatorId } = this.state.currentEntity;
            const currentDamageEstimation: IDamageEstimation = damageEstimationInitialData.EvaluationSteps.filter(e => e.DamageCaseEvaluatorId === DamageCaseEvaluatorId && e.Substatus === enmEvaluationStatus.DamageEstimation && e.HasNextStep !== true)[0] as any;
            const sendingItemsList = currentDamageEstimation
                ? currentDamageEstimation.SendingItems.map((s: any) => {
                    return { Id: s.SendingItemId, IsAvailable: s.IsAvailable, Price: s.Price, Name: s.SendingItem.Name };
                })
                : damageEstimationInitialData.SendingItems;

            this.setState({
                loading: false,
                policyCoverages: damageEstimationInitialData.PolicyCoverages,
                policyFranchises: damageEstimationInitialData.PolicyFranchises,
                evaluationSteps: damageEstimationInitialData.EvaluationSteps,
                sendingItems: damageEstimationInitialData.SendingItems,
                damageTypes: damageEstimationInitialData.DamageTypes,
                damageCaseDocumentTypeAttachments: damageEstimationInitialData.DamageCaseDocumentTypeAttachments,
                confirmPermission: damageEstimationInitialData.ConfirmPermission,
                currentEntity: currentDamageEstimation
                    ? { ...currentDamageEstimation, SendingItemList: sendingItemsList }
                    : { ...this.state.currentEntity, SendingItemList: sendingItemsList },
                policyCert: damageEstimationInitialData.PolicyCert,
                allDamageCases: damageEstimationInitialData.AllDamageCases
            });
        };
    }
    onChangeDamageType = (value: any) => {
        let currentEntity = this.state.currentEntity;
        currentEntity.DamageTypeId = value;
        currentEntity.DamageType = this.state.damageTypes.find(d => d.Id === currentEntity.DamageTypeId)!;
        //(currentEntity as any)['ExpertConfirmation'] = undefined;
        (currentEntity as any)['EssentialsReceived'] = undefined;
        (currentEntity as any)['Franchise1Id'] = undefined;
        (currentEntity as any)['Franchise1'] = undefined;
        (currentEntity as any)['Franchise2Id'] = undefined;
        (currentEntity as any)['Franchise2'] = undefined;
        this.setState({ currentEntity });
        this.getSendingItems(value);
    }
    getSendingItems = (damageTypeId?: number) => {
        const { DamageCaseEvaluatorId } = this.state.currentEntity;
        this.callApi({
            url: "damagecaseevaluator/getsendingitems",
            data: { damageCaseEvaluatorId: DamageCaseEvaluatorId, damageTypeId },
            onSuccess: (result: IResultSet<any>) => {
                if (result.ValidationState.Successful) {
                    const sendingItems: ISendingItem[] = result.Data;

                    this.setState({
                        sendingItems,
                        currentEntity: { ...this.state.currentEntity, SendingItemList: sendingItems },
                    });
                }
            }
        });
    }
    onChangePolicyCoverage = (value: any) => {
        let currentEntity = this.state.currentEntity;
        currentEntity.PolicyCoverageId = value;
        currentEntity.PolicyCoverage = this.state.policyCoverages.find(c => c.Id === value);
        this.setState({ currentEntity });
    }
    //onChangeInsuredValue = (value: any) => {
    //    let currentEntity = this.state.currentEntity;
    //    currentEntity.InsuredValue = value;
    //    calculateAmount(currentEntity);
    //    this.setState({ currentEntity });
    //}
    onChangeEquipmentPrice = (value: any) => {
        let currentEntity = this.state.currentEntity;
        currentEntity.EquipmentPrice = value;
        calculateAmount(currentEntity);
        this.setState({ currentEntity });
    }
    onChangeFranchise1DepreciationPercent = (value: any) => {
        let currentEntity = this.state.currentEntity;
        currentEntity.Franchise1DepreciationPercent = Math.min(100, Math.max(0, value));
        calculateAmount(currentEntity);
        this.setState({ currentEntity });
    }
    onChangeFranchise2DepreciationPercent = (value: any) => {
        let currentEntity = this.state.currentEntity;
        currentEntity.Franchise2DepreciationPercent = Math.min(100, Math.max(0, value));
        calculateAmount(currentEntity);
        this.setState({ currentEntity });
    }
    onChangeSendingItemList = (items: ISendingItem[]) => {
        let currentEntity = this.state.currentEntity;
        currentEntity.SendingItemList = items;
        calculateAmount(currentEntity);
        this.setState({ currentEntity });
    }
    onChangeFranchise1 = (value: any) => {
        let currentEntity = this.state.currentEntity;
        currentEntity.Franchise1Id = value;
        currentEntity.Franchise1 = this.state.policyFranchises.find(f => f.Id === value);
        currentEntity.Franchise1Percent = currentEntity.Franchise1?.FranchisePercent ?? 0;
        calculateAmount(currentEntity);
        this.setState({ currentEntity });
    }
    onChangeFranchise1EstimatedAmount = (value: any) => {
        let currentEntity = this.state.currentEntity;
        currentEntity.Franchise1EstimatedAmount = value;
        calculateAmount(currentEntity);
        this.setState({ currentEntity });
    }
    onChangeFranchise1Description = (e: any) => {
        let currentEntity = this.state.currentEntity;
        currentEntity.Franchise1Description = e.target.value;
        this.setState({ currentEntity });
    }
    //onChangeFranchise1Percent = (value: any) => {
    //    let currentEntity = this.state.currentEntity;
    //    currentEntity.Franchise1Percent = value;
    //    calculateAmount(currentEntity);
    //    this.setState({ currentEntity });
    //}
    onChangeHasFranchise2 = (e: any) => {
        let currentEntity = this.state.currentEntity;
        currentEntity.HasFranchise2 = e.target.value;
        calculateAmount(currentEntity);
        this.setState({ currentEntity });
    }
    onChangeFranchise2 = (value: any) => {
        let currentEntity = this.state.currentEntity;
        currentEntity.Franchise2Id = value;
        currentEntity.Franchise2 = this.state.policyFranchises.find(f => f.Id === value);
        currentEntity.Franchise2Percent = currentEntity.Franchise2?.FranchisePercent ?? 0;
        calculateAmount(currentEntity);
        this.setState({ currentEntity });
    }
    onChangeFranchise2EstimatedAmount = (value: any) => {
        let currentEntity = this.state.currentEntity;
        currentEntity.Franchise2EstimatedAmount = value;
        calculateAmount(currentEntity);
        this.setState({ currentEntity });
    }
    onChangeFranchise2Description = (e: any) => {
        let currentEntity = this.state.currentEntity;
        currentEntity.Franchise2Description = e.target.value;
        this.setState({ currentEntity });
    }
    //onChangeFranchise2Percent = (value: any) => {
    //    let currentEntity = this.state.currentEntity;
    //    currentEntity.Franchise2Percent = value;
    //    calculateAmount(currentEntity);
    //    this.setState({ currentEntity });
    //}
    onChangeExpertConfirmation = (e: any) => {
        let currentEntity = this.state.currentEntity;
        currentEntity.ExpertConfirmation = e.target.value;
        this.setState({ currentEntity });
    }
    onChangeExpertDescription = (e: any) => {
        let currentEntity = this.state.currentEntity;
        currentEntity.ExpertDescription = e.target.value;
        this.setState({ currentEntity });
    }
    onChangeEssentialsReceived = (e: any) => {
        let currentEntity = this.state.currentEntity;
        currentEntity.EssentialsReceived = e.target.value;
        this.setState({ currentEntity });
    }
    onSubmit = async () => {
        let { currentEntity } = this.state;
        if (!currentEntity)
            return;

        let fetchOpt: ICallOptions = {
            url: "damagecaseevaluator/damageestimation",
            data: {
                evaluation: {
                    Id: currentEntity.Id,
                    RowGuid: currentEntity.RowGuid,
                    DamageCaseEvaluatorId: currentEntity.DamageCaseEvaluatorId,
                    PolicyCoverageId: currentEntity.PolicyCoverageId,
                    DamageTypeId: currentEntity.DamageTypeId,
                    InsuredValue: currentEntity.InsuredValue,
                    EquipmentPrice: currentEntity.EquipmentPrice,

                    Franchise1Id: currentEntity.Franchise1Id,
                    Franchise1DepreciationPercent: currentEntity.Franchise1DepreciationPercent,
                    Franchise1EstimatedAmount: currentEntity.Franchise1EstimatedAmount,
                    Franchise1Description: currentEntity.Franchise1Description,
                    Franchise1Amount: currentEntity.Franchise1Amount,

                    HasFranchise2: currentEntity.HasFranchise2,

                    Franchise2Id: currentEntity.HasFranchise2 === true ? currentEntity.Franchise2Id : undefined,
                    Franchise2DepreciationPercent: currentEntity.Franchise2DepreciationPercent,
                    Franchise2EstimatedAmount: currentEntity.HasFranchise2 === true ? currentEntity.Franchise2EstimatedAmount : undefined,
                    Franchise2Description: currentEntity.HasFranchise2 === true ? currentEntity.Franchise2Description : undefined,
                    Franchise2Amount: currentEntity.HasFranchise2 === true ? currentEntity.Franchise2Amount : undefined,

                    PayableAmount: currentEntity.PayableAmount,
                    ExpertConfirmation: currentEntity.ExpertConfirmation,
                    ExpertDescription: currentEntity.ExpertDescription,

                    EssentialsReceived: currentEntity.EssentialsReceived,

                    SendingItems: this.state.sendingItems.map(s => {
                        return {
                            State: (currentEntity.Id ?? 0) === 0 ? enmEntityState.Added : enmEntityState.Modified,
                            SendingItemId: s.Id,
                            Price: s.Price,
                            IsAvailable: !!currentEntity.SendingItemList.find(i => i.Id === s.Id)?.IsAvailable
                        };
                    })
                }
            },
            onSuccess: (result: IResultSet<any>) => {
                if (result.ValidationState.Successful) {
                    if (this.state.confirmPermission === true && currentEntity.ExpertConfirmation !== undefined) {
                        if (currentEntity.ExpertConfirmation === true)
                            Core.successNotification('برآورد خسارت انجام شد، لطفا منتظر تایید بیمه گزار/مشتری باشید');
                        else if (currentEntity.ExpertConfirmation === false)
                            Core.successNotification('خسارت رد شد');
                    }
                    else {
                        if (this.state.confirmPermission === true)
                            Core.warningNotification('برآورد خسارت نیازمند تایید یا رد خسارت است');
                        else
                            Core.successNotification('برآورد خسارت انجام شد، لطفا منتظر تایید کارشناس زبده باشید');
                    }
                    this.props.onClose && this.props.onClose(this);
                }
            }
        };
        return await this.callApi(fetchOpt);
    }
    renderBody() {
        return (
            <DamageEstimationForm
                key="DamageEstimationForm"
                loading={this.state.loading}
                currentEntity={this.state.currentEntity}
                damageCase={this.props.damageCase}
                damageTypes={this.state.damageTypes}
                confirmPermission={this.state.confirmPermission}
                onClose={this.props.onClose}
                onSubmit={this.onSubmit}
                policyCoverages={this.state.policyCoverages}
                policyFranchises={this.state.policyFranchises}
                sendingItems={this.state.sendingItems}
                damageCaseDocumentTypeAttachments={this.state.damageCaseDocumentTypeAttachments}
                onChangePolicyCoverage={this.onChangePolicyCoverage}
                //onChangeInsuredValue={this.onChangeInsuredValue}
                onChangeEquipmentPrice={this.onChangeEquipmentPrice}
                onChangeFranchise1DepreciationPercent={this.onChangeFranchise1DepreciationPercent}
                onChangeFranchise2DepreciationPercent={this.onChangeFranchise2DepreciationPercent}
                onChangeSendingItemList={this.onChangeSendingItemList}
                onChangeFranchise1={this.onChangeFranchise1}
                //onChangeFranchise1Percent={this.onChangeFranchise1Percent}
                onChangeFranchise1Description={this.onChangeFranchise1Description}
                onChangeFranchise1EstimatedAmount={this.onChangeFranchise1EstimatedAmount}
                onChangeHasFranchise2={this.onChangeHasFranchise2}
                onChangeFranchise2={this.onChangeFranchise2}
                //onChangeFranchise2Percent={this.onChangeFranchise2Percent}
                onChangeFranchise2Description={this.onChangeFranchise2Description}
                onChangeFranchise2EstimatedAmount={this.onChangeFranchise2EstimatedAmount}
                onChangeExpertConfirmation={this.onChangeExpertConfirmation}
                onChangeDamageType={this.onChangeDamageType}
                onChangeExpertDescription={this.onChangeExpertDescription}
                onChangeEssentialsReceived={this.onChangeEssentialsReceived}
            />
        );
    }
}